import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User } from "lucide-react";
import { publicApi } from "../lib/api";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

export default function BlogPostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    publicApi.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
    });
    publicApi.get("/blog").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (notFound) return <Navigate to="/journal" replace />;
  if (!post) return <div className="pt-40 pb-40 text-center text-foreground/55">Loading…</div>;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: "Voyage Luxe", logo: { "@type": "ImageObject", url: "https://voyageluxe.com/logo.png" } },
  };

  return (
    <article>
      <Seo title={`${post.title} — Voyage Luxe Journal`} description={post.excerpt} image={post.image} type="article" schema={articleSchema} />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden">
        <img src={post.image} alt={post.title} className="absolute inset-0 h-full w-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/75" />
        <div className="container-luxe relative h-full flex flex-col justify-end pb-16 md:pb-24 text-white">
          <Reveal>
            <div className="eyebrow text-white/65 mb-4">{post.category} · {post.read_time}</div>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="font-serif text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.98] tracking-tight max-w-5xl text-balance">{post.title}</h1>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 flex items-center gap-6 text-white/75 text-sm">
              {post.author_avatar && <img src={post.author_avatar} alt={post.author} className="h-10 w-10 rounded-full object-cover" />}
              <div className="flex items-center gap-2"><User size={13} strokeWidth={1.25} />{post.author}</div>
              <div className="flex items-center gap-2"><Calendar size={13} strokeWidth={1.25} />{post.date}</div>
              <div className="flex items-center gap-2"><Clock size={13} strokeWidth={1.25} />{post.read_time}</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="container-luxe py-6 text-[11px] uppercase tracking-[0.25em] text-foreground/50">
        <Link to="/" className="hover:text-accent">Home</Link> · <Link to="/journal" className="hover:text-accent">Journal</Link> · <span className="text-foreground">{post.category}</span>
      </nav>

      {/* Content */}
      <section className="container-luxe pb-16 max-w-3xl">
        <Reveal>
          <p className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/85 border-l-2 border-accent pl-6 mb-12">
            {post.excerpt}
          </p>
        </Reveal>
        <Reveal>
          <div
            className="prose-luxe text-lg leading-[1.85] text-foreground/85 space-y-6 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:tracking-tight [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-5 [&_em]:italic [&_em]:text-accent"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Reveal>
        {post.tags?.length > 0 && (
          <Reveal>
            <div className="mt-16 pt-8 border-t border-border">
              <div className="eyebrow mb-4">Tagged</div>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="px-4 py-2 border border-border text-[11px] uppercase tracking-[0.22em] text-foreground/65">{t}</span>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </section>

      {/* Author card */}
      <section className="container-luxe pb-24 max-w-3xl">
        <Reveal>
          <div className="border border-border bg-secondary/30 p-8 md:p-10 flex items-center gap-6">
            {post.author_avatar && <img src={post.author_avatar} alt={post.author} className="h-16 w-16 rounded-full object-cover shrink-0" />}
            <div>
              <div className="eyebrow mb-1">Written by</div>
              <div className="font-serif text-2xl">{post.author}</div>
              <p className="mt-2 text-sm text-foreground/65">Atelier writer at Voyage Luxe.</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-secondary/30">
          <div className="container-luxe">
            <Reveal>
              <div className="flex items-end justify-between mb-10">
                <h2 className="font-serif text-2xl md:text-3xl">Continue reading</h2>
                <Link to="/journal" className="eyebrow hover:text-accent">All entries →</Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {related.map((p, i) => (
                <Reveal key={p.id || p.slug} delay={i * 80}>
                  <Link to={`/journal/${p.slug}`} className="group block">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    </div>
                    <div className="mt-4 eyebrow text-foreground/55">{p.category} · {p.read_time}</div>
                    <h3 className="mt-1 font-serif text-2xl group-hover:text-accent transition-colors">{p.title}</h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back link */}
      <div className="container-luxe pb-24">
        <Link to="/journal" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-foreground/60 hover:text-accent">
          <ArrowLeft size={14} strokeWidth={1.25} /> Back to journal
        </Link>
      </div>
    </article>
  );
}
